import { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import ConfirmationDialog from "../../shared/ConfirmationDialog";
import FormCheckbox from "../../shared/FormCheckbox";
import FormSelect from "../../shared/FormSelect";
import Modal from "../../modal/Modal";
import FieldAssignments from "./FieldAssignments";
import {
    CATEGORIES,
    DATA_TYPES,
    PRIORITY_RULES,
    SCOPES,
    assignmentInputError,
    buildFieldBody,
    fieldKeyFromLabel,
    formValuesFromField,
    validateFieldForm,
} from "./fieldForm";
import { withFieldError } from "./formErrors";
import "./FieldAdmin.scss";

const BINDING_LABELS = {
    "client.displayName": "the client name",
    "client.groupId": "the group ID",
    "plan.planId": "the plan ID",
    "plan.displayName": "the plan name",
};

const CATEGORY_OPTIONS = CATEGORIES.map((category) => ({ value: category, label: category }));
const DATA_TYPE_OPTIONS = DATA_TYPES.map((dataType) => ({ value: dataType, label: dataType }));
const STATUS_OPTIONS = [
    { value: "ACTIVE", label: "Active" },
    { value: "INACTIVE", label: "Inactive" },
];

const optionFor = (options, value) => options.find((option) => option.value === value) ?? null;

const selectErrors = (errors) =>
    Object.fromEntries(
        ["category", "dataType", "scope", "status", "priorityRule"]
            .filter((name) => errors[name])
            .map((name) => [name, { message: errors[name] }]),
    );

const FieldFormDialog = ({ field, documentTypes, saving, serverError, onClose, onSubmit }) => {
    const isCreate = !field;
    const firstFieldRef = useRef(null);
    const [keyTouched, setKeyTouched] = useState(false);
    const keyTouchedRef = useRef(false);
    const [values, setValues] = useState(() => formValuesFromField(field, documentTypes));
    const valuesRef = useRef(values);
    valuesRef.current = values;
    const [errors, setErrors] = useState({});
    const [scopeBody, setScopeBody] = useState(null);
    const typeByKey = new Map(documentTypes.map((documentType) => [documentType.documentTypeKey, documentType]));
    const { control, getValues } = useForm({
        defaultValues: {
            category: optionFor(CATEGORY_OPTIONS, values.category),
            dataType: optionFor(DATA_TYPE_OPTIONS, values.dataType),
            scope: optionFor(SCOPES, values.scope),
            status: optionFor(STATUS_OPTIONS, values.status),
            priorityRule: optionFor(PRIORITY_RULES, values.priorityRule),
            universal: values.universal,
        },
    });

    useEffect(() => {
        firstFieldRef.current?.focus();
    }, []);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === "Escape" && !saving) onClose();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [onClose, saving]);

    const setField = (name) => (event) => {
        setValues((current) => ({ ...current, [name]: event.target.value }));
    };

    const setDisplayName = (event) => {
        const displayName = event.target.value;
        setValues((current) => ({
            ...current,
            displayName,
            fieldKey: isCreate && !keyTouched ? fieldKeyFromLabel(displayName) : current.fieldKey,
        }));
    };

    const fieldErrors = (snapshot) => {
        const nextErrors = validateFieldForm(snapshot, { requireKey: isCreate });
        if (!snapshot.priorityRule) nextErrors.priorityRule = "Priority is required.";
        if (snapshot.status !== "ACTIVE" && snapshot.status !== "INACTIVE") nextErrors.status = "Status is required.";
        return nextErrors;
    };

    const snapshot = (overrides = {}) => {
        const selected = getValues();
        return {
            ...valuesRef.current,
            category: selected.category?.value ?? "",
            dataType: selected.dataType?.value ?? "",
            scope: selected.scope?.value ?? "",
            status: selected.status?.value ?? "",
            priorityRule: selected.priorityRule?.value ?? "",
            universal: Boolean(selected.universal),
            ...overrides,
        };
    };

    const reportFieldError = (name, nextSnapshot) => {
        setErrors((current) => withFieldError(current, name, fieldErrors(nextSnapshot)[name] || ""));
    };

    const changeText = (name) => (event) => {
        setField(name)(event);
        reportFieldError(name, snapshot({ [name]: event.target.value }));
    };

    const changeDisplayName = (event) => {
        setDisplayName(event);
        const displayName = event.target.value;
        const fieldKey = isCreate && !keyTouchedRef.current ? fieldKeyFromLabel(displayName) : valuesRef.current.fieldKey;
        const nextSnapshot = snapshot({ displayName, fieldKey });
        reportFieldError("displayName", nextSnapshot);
        setErrors((current) => (current.fieldKey ? withFieldError(current, "fieldKey", fieldErrors(nextSnapshot).fieldKey || "") : current));
    };

    const blurChoice = (name) => () => {
        reportFieldError(name, snapshot());
    };

    const changeChoice = (name) => (option) => {
        reportFieldError(name, snapshot({ [name]: option?.value ?? "" }));
    };

    const updateAssignment = (documentTypeKey, patch) => {
        const documentTypes = valuesRef.current.documentTypes.map((assignment) =>
            assignment.documentTypeKey === documentTypeKey ? { ...assignment, ...patch } : assignment,
        );
        setValues((current) => ({
            ...current,
            documentTypes: current.documentTypes.map((assignment) =>
                assignment.documentTypeKey === documentTypeKey ? { ...assignment, ...patch } : assignment,
            ),
        }));
        const edited = documentTypes.find((assignment) => assignment.documentTypeKey === documentTypeKey);
        if ("extractionOrder" in patch || "overrideConfidenceThreshold" in patch) {
            const fieldName = "extractionOrder" in patch ? "extractionOrder" : "overrideConfidenceThreshold";
            const message = assignmentInputError(edited, fieldName);
            setErrors((current) => {
                if (message) return withFieldError(current, "assignments", message);
                if (!current.assignments) return current;
                return withFieldError(current, "assignments", fieldErrors(snapshot({ documentTypes })).assignments || "");
            });
        }
        if ("selected" in patch) {
            const anySelected = documentTypes.some((assignment) => assignment.selected);
            setErrors((current) => {
                if (!anySelected) return withFieldError(current, "assignments", "At least one document type is required.");
                if (!current.assignments) return current;
                return withFieldError(current, "assignments", fieldErrors(snapshot({ documentTypes })).assignments || "");
            });
        }
    };

    const watched = useWatch({ control }) ?? {};
    const formInvalid = Object.keys(fieldErrors({
        ...values,
        category: watched.category?.value ?? "",
        dataType: watched.dataType?.value ?? "",
        scope: watched.scope?.value ?? "",
        status: watched.status?.value ?? "",
        priorityRule: watched.priorityRule?.value ?? "",
        universal: Boolean(watched.universal),
    })).length > 0;

    const handleSubmit = (event) => {
        event.preventDefault();
        if (saving) return;
        const nextValues = snapshot();
        const nextErrors = fieldErrors(nextValues);
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) return;
        const body = buildFieldBody(nextValues);
        if (!isCreate) delete body.fieldKey;
        if (!isCreate && field.scope !== nextValues.scope) {
            setScopeBody(body);
            return;
        }
        onSubmit(body);
    };

    const bindingLabel = BINDING_LABELS[field?.identityBinding];
    const choiceErrors = selectErrors(errors);

    return (
        <Modal isOpen className="modal-content-compact cdes-field-modal" onClose={saving ? undefined : onClose}>
            <form className="cdes-client-form cdes-field-form" aria-label={isCreate ? "Add field" : "Edit field"} onSubmit={handleSubmit} noValidate>
                <div className="cdes-field-form__body">
                <p className="title">{isCreate ? "Add Field" : "Edit Field"}</p>
                <p className="cdes-field-form__section">Identity</p>
                <div className="form-input">
                    <label htmlFor="cdes-field-name">
                        Display name <span className="danger">*</span>
                    </label>
                    <input
                        id="cdes-field-name"
                        ref={firstFieldRef}
                        value={values.displayName}
                        onChange={changeDisplayName}
                        disabled={saving}
                        aria-invalid={Boolean(errors.displayName) || undefined}
                        placeholder="e.g. Client Name"
                        autoComplete="off"
                    />
                    {errors.displayName ? (
                        <p className="danger cdes-client-form__error" role="alert">
                            {errors.displayName}
                        </p>
                    ) : null}
                </div>
                <div className="form-input">
                    <label htmlFor="cdes-field-key">
                        Key {isCreate ? <span className="danger">*</span> : null}
                    </label>
                    <input
                        id="cdes-field-key"
                        value={values.fieldKey}
                        onChange={(event) => {
                            keyTouchedRef.current = true;
                            setKeyTouched(true);
                            setField("fieldKey")(event);
                            reportFieldError("fieldKey", snapshot({ fieldKey: event.target.value }));
                        }}
                        disabled={!isCreate || saving}
                        readOnly={!isCreate}
                        aria-invalid={Boolean(errors.fieldKey) || undefined}
                        aria-describedby="cdes-field-key-help"
                        placeholder="e.g. client-name"
                        autoComplete="off"
                    />
                    <p id="cdes-field-key-help" className="cdes-client-form__help">
                        The key cannot be changed after the field is created.
                    </p>
                    {errors.fieldKey ? (
                        <p className="danger cdes-client-form__error" role="alert">
                            {errors.fieldKey}
                        </p>
                    ) : null}
                </div>
                {bindingLabel ? (
                    <p className="cdes-client-form__help">This field is bound to {bindingLabel}. It stays out of the regular data columns.</p>
                ) : null}

                <p className="cdes-field-form__section">Classification</p>
                <div className="cdes-field-form__row">
                    <FormSelect
                        name="category"
                        label="Category"
                        required
                        control={control}
                        options={CATEGORY_OPTIONS}
                        errors={choiceErrors}
                        placeholder="Category"
                        disabled={saving}
                        isClearable={false}
                        onSelectedHandler={changeChoice("category")}
                        onBlurHandler={blurChoice("category")}
                    />
                    <FormSelect
                        name="dataType"
                        label="Data type"
                        required
                        control={control}
                        options={DATA_TYPE_OPTIONS}
                        errors={choiceErrors}
                        placeholder="Data type"
                        disabled={saving}
                        isClearable={false}
                        onSelectedHandler={changeChoice("dataType")}
                        onBlurHandler={blurChoice("dataType")}
                    />
                    <FormSelect
                        name="scope"
                        label="Scope"
                        required
                        control={control}
                        options={SCOPES}
                        errors={choiceErrors}
                        placeholder="Scope"
                        disabled={saving}
                        isClearable={false}
                        onSelectedHandler={changeChoice("scope")}
                        onBlurHandler={blurChoice("scope")}
                    />
                    <FormSelect
                        name="status"
                        label="Status"
                        required
                        control={control}
                        options={STATUS_OPTIONS}
                        errors={choiceErrors}
                        placeholder="Status"
                        disabled={saving}
                        isClearable={false}
                        onSelectedHandler={changeChoice("status")}
                        onBlurHandler={blurChoice("status")}
                    />
                </div>
                <FormCheckbox name="universal" label="Universal" control={control} disabled={saving} />
                <p className="cdes-client-form__help">
                    When this field is assigned to a document type, a future extraction prompt places it in a separate matching section. Universal does not assign the field to every document type.
                </p>

                <p className="cdes-field-form__section">Configuration</p>
                <div className="cdes-field-form__row">
                    <div className="form-input">
                        <label htmlFor="cdes-field-confidence">Confidence threshold</label>
                        <input
                            id="cdes-field-confidence"
                            value={values.confidenceThreshold}
                            onChange={changeText("confidenceThreshold")}
                            disabled={saving}
                            inputMode="decimal"
                            placeholder="e.g. 0.8"
                            aria-invalid={Boolean(errors.confidenceThreshold) || undefined}
                        />
                        {errors.confidenceThreshold ? (
                            <p className="danger cdes-client-form__error" role="alert">
                                {errors.confidenceThreshold}
                            </p>
                        ) : null}
                    </div>
                    <FormSelect
                        name="priorityRule"
                        label="Priority"
                        required
                        control={control}
                        options={PRIORITY_RULES}
                        errors={choiceErrors}
                        placeholder="Priority"
                        disabled={saving}
                        isClearable={false}
                        onSelectedHandler={changeChoice("priorityRule")}
                        onBlurHandler={blurChoice("priorityRule")}
                    />
                    <div className="form-input">
                        <label htmlFor="cdes-field-order">Sort order</label>
                        <input
                            id="cdes-field-order"
                            value={values.sortOrder}
                            onChange={changeText("sortOrder")}
                            disabled={saving}
                            inputMode="numeric"
                            placeholder="e.g. 10"
                            aria-invalid={Boolean(errors.sortOrder) || undefined}
                        />
                        {errors.sortOrder ? (
                            <p className="danger cdes-client-form__error" role="alert">
                                {errors.sortOrder}
                            </p>
                        ) : null}
                    </div>
                </div>

                <p className="cdes-field-form__section">Guidance</p>
                <div className="form-input">
                    <label htmlFor="cdes-field-labels">Common labels</label>
                    <textarea
                        id="cdes-field-labels"
                        value={values.commonLabels}
                        onChange={setField("commonLabels")}
                        disabled={saving}
                        rows={3}
                        placeholder={"Labels that appear before this data in documents. One per line.\ne.g.\nClient:\nEmployer:\nCompany Name:"}
                    />
                    <p className="cdes-client-form__help">One label per line.</p>
                </div>
                <div className="form-input">
                    <label htmlFor="cdes-field-where">Where to look</label>
                    <textarea
                        id="cdes-field-where"
                        value={values.whereToLook}
                        onChange={setField("whereToLook")}
                        disabled={saving}
                        rows={2}
                        placeholder="e.g. Page 1 header, opening paragraph, fee schedule table"
                    />
                </div>
                <div className="form-input">
                    <label htmlFor="cdes-field-examples">Examples</label>
                    <textarea
                        id="cdes-field-examples"
                        value={values.examples}
                        onChange={setField("examples")}
                        disabled={saving}
                        rows={3}
                        placeholder={"One example value per line.\ne.g.\nAcme Corp\nBeta Inc."}
                    />
                    <p className="cdes-client-form__help">One example per line.</p>
                </div>
                <div className="form-input">
                    <label htmlFor="cdes-field-notes">Extraction notes</label>
                    <textarea
                        id="cdes-field-notes"
                        value={values.extractionNotes}
                        onChange={setField("extractionNotes")}
                        disabled={saving}
                        rows={2}
                        placeholder="Additional context, edge cases, disambiguation hints…"
                    />
                </div>
                <div className="form-input">
                    <label htmlFor="cdes-field-format">Format pattern</label>
                    <input
                        id="cdes-field-format"
                        value={values.formatPattern}
                        onChange={setField("formatPattern")}
                        disabled={saving}
                        placeholder="e.g. YYYY-MM-DD  /  Dollar amount PEPM (no $ sign)"
                    />
                </div>

                <FieldAssignments
                    assignments={values.documentTypes}
                    typeByKey={typeByKey}
                    saving={saving}
                    errors={errors.assignments}
                    onChange={updateAssignment}
                />
                {serverError ? (
                    <p className="danger cdes-client-form__error" role="alert">
                        {serverError}
                    </p>
                ) : null}
                </div>
                <div className="cdes-field-form__footer">
                    <button type="button" className="outline-btn" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button type="submit" className="edit-btn" disabled={saving || formInvalid}>
                        {saving ? "Saving…" : isCreate ? "Create Field" : "Save Changes"}
                    </button>
                </div>
            </form>
            <ConfirmationDialog
                open={Boolean(scopeBody)}
                onOpenChange={(open) => {
                    if (!open) setScopeBody(null);
                }}
                title="Change field scope"
                body="Changing the scope does not rewrite stored values."
                confirmLabel="Change scope"
                confirmPendingLabel="Saving…"
                onConfirm={() => onSubmit(scopeBody)}
            />
        </Modal>
    );
};

export default FieldFormDialog;
